import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";

export const formatObservationDate = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  const day = date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Europe/Paris",
  });
  const time = date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Paris",
  });
  return `${day} ${time}`;
};

export const approverRoleLabel = (role) => {
  switch (role) {
    case "FATHER":
    case "MOTHER":
      return "Parent";
    case "STUDENT":
      return "Élève";
    default:
      return "";
  }
};

export const approverDisplayName = (observation) =>
  observation?.approved_by?.name || approverRoleLabel(observation?.approved_by_role);

export const notificationLabel = (observation) => {
  switch (observation?.notification_status) {
    case "SENT":
      return { value: `Email envoyé (${observation?.notification_recipients || 0})`, severity: "success" };
    case "SENDING":
      return { value: "Envoi en cours", severity: "info" };
    case "SKIPPED":
      return { value: "Aucun email envoyé", severity: "warning" };
    case "FAILED":
      return { value: "Échec d'envoi", severity: "danger" };
    default:
      return null;
  }
};

export const ObservationVisibility = ({ observation }) => {
  if (!observation?.notified) {
    return <Tag value="Interne" severity="secondary" icon="pi pi-lock" />;
  }
  const tag = notificationLabel(observation);
  const hasEmailIssue = ["FAILED", "SKIPPED"].includes(observation?.notification_status);
  const targetId = `observation-visibility-${observation.id}`;
  return (
    <>
      <Tooltip target={`#${targetId}`} position="left">
        <div className="flex flex-column gap-1 text-sm">
          <span>Partagée le {formatObservationDate(observation?.notified_at)}</span>
          {observation?.notified_by?.name && <span>Par {observation.notified_by.name}</span>}
          {tag && <span>{tag.value}</span>}
        </div>
      </Tooltip>
      <Tag
        id={targetId}
        data-testid={targetId}
        value="Partagée"
        severity={hasEmailIssue ? tag?.severity : "info"}
        icon={hasEmailIssue ? "pi pi-exclamation-triangle" : "pi pi-users"}
        className="cursor-help"
      />
    </>
  );
};

export const ObservationApproval = ({ observation, onShowResponse }) => {
  if (!observation?.notified) {
    return "-";
  }
  if (!observation?.approved) {
    return <Tag value="En attente de lecture" severity="warning" icon="pi pi-clock" />;
  }
  const approver = approverDisplayName(observation);
  return (
    <div className="flex flex-column gap-1">
      <div className="flex align-items-center gap-1">
        <Tag value="Lue par la famille" severity="success" icon="pi pi-check" />
        {observation?.approval_response && onShowResponse && (
          <Button
            icon="pi pi-envelope"
            rounded
            text
            size="small"
            aria-label="Voir la réponse de la famille"
            tooltip="Voir la réponse de la famille"
            tooltipOptions={{ position: "top" }}
            onClick={() => onShowResponse(observation)}
          />
        )}
      </div>
      <small className="text-600">
        {formatObservationDate(observation?.approved_at)}
        {approver && ` — ${approver}`}
      </small>
    </div>
  );
};

export const ObservationResponseDialog = ({ observation, onHide }) => (
  <Dialog
    header="Réponse de la famille"
    visible={!!observation}
    style={{ width: "40vw" }}
    breakpoints={{ "960px": "75vw", "641px": "100vw" }}
    modal
    onHide={onHide}
    footer={<Button label="Fermer" icon="pi pi-times" text onClick={onHide} />}
  >
    {observation && (
      <div className="flex flex-column gap-3">
        <small className="text-600">
          {approverDisplayName(observation)}
          {approverDisplayName(observation) && " — "}
          {formatObservationDate(observation?.approved_at)}
        </small>
        <div className="line-height-3" style={{ whiteSpace: "pre-line" }}>
          {observation.approval_response}
        </div>
        <div className="surface-50 border-round p-2 text-sm text-600">
          <div className="font-medium mb-1">Observation du {formatObservationDate(observation?.issued_at)}</div>
          <div style={{ whiteSpace: "pre-line" }}>{observation.message}</div>
        </div>
      </div>
    )}
  </Dialog>
);
